import { useEffect, useRef, useState } from 'react';

export type EventSourceStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface UseEventSourceOptions {
  /** Named events to subscribe to (in addition to the default `message` channel). */
  events?: string[];
  /** Called for every event (default or named). `type === 'message'` for unnamed events. */
  onEvent?: (type: string, data: unknown, raw: MessageEvent) => void;
  /** Backoff before reconnecting after error. Default: 5000 ms. */
  reconnectDelayMs?: number;
  /** Disable the connection without unmounting. */
  enabled?: boolean;
  /** Pause the stream when the tab is hidden. Default: true. */
  pauseWhenHidden?: boolean;
  /** Optional key to force a re-connect when it changes (e.g., token rotation). */
  connectionKey?: string | number | null;
}

/**
 * Generic EventSource hook with reconnect + visibility-aware pause.
 * Keeps the raw MessageEvent available so callers can read `lastEventId`.
 * Parses JSON payloads automatically; falls back to the raw string when parse fails.
 */
export function useEventSource(url: string | null, options: UseEventSourceOptions = {}) {
  const {
    events,
    onEvent,
    reconnectDelayMs = 5000,
    enabled = true,
    pauseWhenHidden = true,
    connectionKey,
  } = options;

  const [status, setStatus] = useState<EventSourceStatus>('idle');
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);
  const eventsRef = useRef(events);

  onEventRef.current = onEvent;
  eventsRef.current = events;

  useEffect(() => {
    if (!enabled || !url) {
      setStatus('idle');
      return;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      if (pauseWhenHidden && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        setStatus('idle');
        return;
      }
      setStatus('connecting');
      const es = new EventSource(url);
      esRef.current = es;

      const handleEvent = (type: string) => (ev: MessageEvent) => {
        let data: unknown = ev.data;
        if (typeof ev.data === 'string') {
          try {
            data = JSON.parse(ev.data);
          } catch {
            data = ev.data;
          }
        }
        onEventRef.current?.(type, data, ev);
      };

      es.onopen = () => {
        if (cancelled) return;
        setStatus('open');
      };

      es.onerror = () => {
        if (cancelled) return;
        setStatus('error');
        es.close();
        esRef.current = null;
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(connect, reconnectDelayMs);
      };

      es.addEventListener('message', handleEvent('message'));
      (eventsRef.current ?? []).forEach((evName) => {
        es.addEventListener(evName, handleEvent(evName));
      });
    };

    connect();

    const onVisibility = () => {
      if (!pauseWhenHidden) return;
      if (document.visibilityState === 'hidden') {
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
          setStatus('closed');
        }
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      } else if (document.visibilityState === 'visible' && !esRef.current) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setStatus('closed');
    };
  }, [url, enabled, reconnectDelayMs, pauseWhenHidden, connectionKey]);

  return { status };
}
